import { useRef, useState } from "react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { CHART_AXIS, CHART_GRID, CHART_TOOLTIP, CS_COLORS } from "@/lib/chartTheme";
import { formatDateTime } from "@/lib/format";
import { DEFAULT_FILTERS, mergeFilters } from "./explorerQuery";
import { ProblemsExplorer } from "./ProblemsExplorer";
import { CLASS_LABEL, type ErrorStats, type ExplorerFilters, type KeyCount, type ProblemUser, type UploadStats } from "./types";
import { Bar, Card } from "./ui";

function KeyCountList({
  items, labels, tone, onSelect,
}: { items: KeyCount[]; labels?: Record<string, string>; tone: "rose" | "amber"; onSelect?: (key: string) => void }) {
  const max = items.reduce((m, i) => Math.max(m, i.count), 0);
  if (items.length === 0) return <p className="text-sm text-[var(--cs-ink-muted)]">Geen</p>;
  return (
    <ul className="space-y-2">
      {items.map((i) => (
        <li key={i.key}>
          <div className="mb-1 flex justify-between text-xs">
            {onSelect ? (
              <button
                type="button"
                onClick={() => { onSelect(i.key); }}
                className="cursor-pointer text-left text-[var(--cs-navy)] underline-offset-2 hover:underline"
              >
                {labels?.[i.key] ?? i.key}
              </button>
            ) : (
              <span className="text-[var(--cs-navy)]">{labels?.[i.key] ?? i.key}</span>
            )}
            <span className="tabular-nums text-[var(--cs-ink-muted)]">{i.count}</span>
          </div>
          <Bar value={i.count} max={max} tone={tone} />
        </li>
      ))}
    </ul>
  );
}

export function ProblemsSection({
  errors, uploads, problemUsers, days, onUserClick,
}: {
  errors: ErrorStats; uploads: UploadStats; problemUsers: ProblemUser[]; days: number;
  onUserClick: (userId: string, email: string) => void;
}) {
  const [filters, setFilters] = useState<ExplorerFilters>(DEFAULT_FILTERS);
  const explorerRef = useRef<HTMLDivElement | null>(null);

  // Every card is a filter source: merge the clicked slice into the explorer
  // filters and bring the explorer into view.
  const drill = (patch: Partial<ExplorerFilters>) => {
    setFilters((f) => mergeFilters(f, patch));
    explorerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section className="space-y-4">
      <p className="cs-caption">Problemen</p>
      <div className="grid gap-4 lg:grid-cols-3">
        <Card caption="Fouten per dag" className="lg:col-span-2">
          <div className="h-[200px] cursor-pointer">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={errors.by_day}
                onClick={(state) => { if (typeof state.activeLabel === "string") drill({ kind: "error", date: state.activeLabel }); }}
              >
                <CartesianGrid {...CHART_GRID} />
                <XAxis dataKey="date" {...CHART_AXIS} />
                <YAxis {...CHART_AXIS} allowDecimals={false} />
                <Tooltip {...CHART_TOOLTIP} />
                <Area type="monotone" dataKey="count" stroke={CS_COLORS.rose} fill={CS_COLORS.rose} fillOpacity={0.12} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card caption="Fouten per klasse">
          <KeyCountList items={errors.by_class} labels={CLASS_LABEL} tone="rose" onSelect={(key) => { drill({ kind: "error", error_class: key }); }} />
        </Card>
        <Card caption="Fouten per bron (top 10)">
          <KeyCountList items={errors.by_source} tone="rose" onSelect={(key) => { drill({ kind: "error", source: key }); }} />
        </Card>
        <Card caption="Mislukte uploads per klasse">
          <KeyCountList items={uploads.by_class} labels={CLASS_LABEL} tone="amber" onSelect={(key) => { drill({ kind: "upload_failed", error_class: key }); }} />
          <p className="mt-3 text-xs text-[var(--cs-ink-muted)]">
            {uploads.queued} in wachtrij gezet · {uploads.failed} mislukte pogingen · {uploads.permanent} definitief geparkeerd ·{" "}
            {uploads.recovered} hersteld · {uploads.server_failed_recordings ?? 0} serverzijdig mislukt
          </p>
        </Card>
        <Card caption="Gebruikers met de meeste problemen">
          {problemUsers.length === 0 ? (
            <p className="text-sm text-[var(--cs-ink-muted)]">Geen</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-[var(--cs-ink-muted)]">
                  <th className="py-1">Gebruiker</th>
                  <th className="py-1 text-right">Fouten</th>
                  <th className="py-1 text-right">Uploads</th>
                  <th className="py-1">Laatst</th>
                </tr>
              </thead>
              <tbody>
                {problemUsers.map((u) => (
                  <tr
                    key={u.user_id}
                    onClick={() => { drill({ user: { id: u.user_id, email: u.email }, kind: u.errors > 0 ? "error" : "upload_failed" }); }}
                    className="cursor-pointer border-t border-[rgba(10,52,96,0.06)] hover:bg-[var(--cs-ivory)]"
                  >
                    <td className="py-2 text-[var(--cs-navy)]">{u.email || u.user_id.slice(0, 8)}</td>
                    <td className="py-2 text-right tabular-nums">{u.errors}</td>
                    <td className="py-2 text-right tabular-nums">{u.failed_uploads}</td>
                    <td className="py-2 text-xs text-[var(--cs-ink-muted)]">{formatDateTime(u.last_seen)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
      <ProblemsExplorer filters={filters} onFiltersChange={setFilters} days={days} onUserClick={onUserClick} anchorRef={explorerRef} />
    </section>
  );
}
